import React from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl w-full',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  full: 'max-w-full'
} as const;

type ModalSize = keyof typeof sizeClasses;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: ModalSize;
  children: React.ReactNode;
  actions?: React.ReactNode;
  zIndex?: number; // Egyedi z-index támogatás
  onExplicitClose?: () => void; // Explicit bezárás X gombhoz
  noPadding?: boolean; // saját belső elrendezésű tartalomhoz (pl. oldalsávos nézet)
}

const LARGE_SIZES: ModalSize[] = ['4xl', '5xl', '6xl', '7xl', 'full'];

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, size = 'md', actions, zIndex = 50, onExplicitClose, noPadding = false }) => {
  // Mobilon: nagy modal = teljes képernyős lap; kicsi (megerősítés, rövid űrlap) = alulról felúszó lap
  const isLarge = LARGE_SIZES.includes(size);
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative" style={{ zIndex }} onClose={onClose} static>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 dark:bg-black/60" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto" style={{ zIndex }}>
          <div className={`flex min-h-full ${isLarge ? 'items-stretch' : 'items-end'} sm:items-center justify-center p-0 sm:p-4`}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className={`w-full ${sizeClasses[size]} transform overflow-hidden ${isLarge ? 'rounded-none min-h-[100dvh] sm:h-[86vh]' : 'rounded-t-2xl max-h-[92dvh]'} sm:rounded-xl sm:min-h-0 sm:max-h-none bg-[var(--crm-surface)] border-0 sm:border border-[var(--crm-border)] shadow-xl transition-all mx-auto flex flex-col`}>
                <div className="border-b border-[var(--crm-border)] flex-shrink-0">
                  <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5">
                    <Dialog.Title className="text-[15px] sm:text-base font-semibold text-gray-900 dark:text-gray-50 truncate">
                      {title}
                    </Dialog.Title>
                    <div className="crm-header-actions flex items-center gap-1.5 flex-shrink-0">
                      {actions}
                      <button
                        type="button"
                        aria-label="Bezárás"
                        className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800 transition-colors"
                        onClick={() => {
                          if (onExplicitClose) {
                            onExplicitClose();
                          } else {
                            onClose();
                          }
                        }}
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className={`flex-1 min-h-0 ${isLarge ? 'max-h-[calc(100dvh-3.25rem)]' : 'max-h-[calc(92dvh-3.25rem)]'} sm:max-h-[calc(100vh-7rem)] overflow-y-auto ${noPadding ? '' : 'p-4 sm:p-6'} [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 dark:[&::-webkit-scrollbar-track]:bg-gray-700 [&::-webkit-scrollbar-thumb]:bg-gray-300 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-primary-500/50`}>
                  {children}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Modal; 